import { FC, ReactNode, useMemo } from "react";

import {
  IsoLocale,
  getLocalesFromIsoLocale,
  CanonicalTagsConfig,
  UsaIsoLocale,
  getOpenGraphDescription,
  getOpenGraphImage,
  getOpenGraphLocale,
  OpenGraphImageFit,
} from "@simplyk/common";
import NextHead from "next/head";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
// eslint-disable-next-line no-restricted-imports
import { i18n } from "next-i18next";

import { isProduction } from "../../../common/constants/env";
import { favicon } from "../../../common/constants/favicon";
import { shouldIndexPage } from "../../../common/helpers/seo";

import { CanonicalTags } from "./CanonicalTags";

export interface ZeffyHeadProps {
  description?: string;
  disableMobileAutoZoom?: boolean;
  image?: string;
  imageFit?: OpenGraphImageFit;
  themeColor?: string;
  title?: string;
  video?: string;
  children?: ReactNode;
  noIndex?: boolean;
  metaTitle?: string;
  metaDescription?: string;
  canonicalTags?: CanonicalTagsConfig;
}

export const ZeffyHead: FC<ZeffyHeadProps> = ({
  description: unparsedDescription = "",
  disableMobileAutoZoom = true,
  image,
  imageFit = "cover",
  themeColor,
  title: inputTitle = "Zeffy",
  video,
  children,
  noIndex,
  metaTitle,
  metaDescription,
  canonicalTags,
}) => {
  // Robust Fallback in case an falsy value is passed as a title
  const currentTitle = inputTitle ? inputTitle : "Zeffy";
  const router = useRouter();

  const pathname = usePathname();
  const isoLocale = (i18n?.language || router.locale || UsaIsoLocale.EN) as IsoLocale;
  const language = getLocalesFromIsoLocale(isoLocale);
  const openGraphLocale = getOpenGraphLocale(isoLocale);

  const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL as string;
  const fallbackCanonicalUrl = `${frontendUrl}${pathname}`;
  const mainCanonicalUrl = canonicalTags?.url ?? fallbackCanonicalUrl;

  const description = useMemo(
    () =>
      getOpenGraphDescription(unparsedDescription) ||
      getOpenGraphDescription(metaDescription) ||
      getOpenGraphDescription(currentTitle),
    [unparsedDescription, metaDescription, currentTitle]
  );

  const openGraphImage = useMemo(() => getOpenGraphImage(image, { fit: imageFit }), [image, imageFit]);

  const viewPortContent = disableMobileAutoZoom
    ? "width=device-width, initial-scale=1, maximum-scale=1"
    : "width=device-width, initial-scale=1";

  // Auto-determine noIndex based on sitemap whitelist if not explicitly set
  const shouldNoIndex = noIndex ?? !shouldIndexPage(pathname || "");

  return (
    <NextHead>
      {(!isProduction || shouldNoIndex) && <meta name="robots" content="noindex, nofollow" />}
      <title>{currentTitle}</title>
      <link rel="icon" href={favicon} />
      <link rel="manifest" href="/manifest.json" />

      <meta charSet="utf-8" />

      <meta name="author" content="Zeffy" />
      <meta name="copyright" content="Zeffy" />
      <meta name="description" content={metaDescription || description} />
      <meta name="language" content={language} />
      {themeColor && (
        <>
          <meta name="theme-color" content={themeColor} />
          <style>{`body{background-color:${themeColor};}`}</style>
        </>
      )}
      <meta name="title" content={metaTitle || currentTitle} />
      <meta name="viewport" content={viewPortContent} />

      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="Zeffy" />
      <meta property="og:title" content={currentTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={mainCanonicalUrl} />
      <meta property="og:locale" content={openGraphLocale} />
      {openGraphImage && (
        <>
          <meta property="og:image" content={openGraphImage.url} />
          <meta property="og:image:secure_url" content={openGraphImage.url} />
          {openGraphImage.mimeType && <meta property="og:image:type" content={openGraphImage.mimeType} />}
          {openGraphImage.width && <meta property="og:image:width" content={String(openGraphImage.width)} />}
          {openGraphImage.height && <meta property="og:image:height" content={String(openGraphImage.height)} />}
          <meta property="og:image:alt" content={currentTitle} />
        </>
      )}
      {video && <meta property="og:video" content={video} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentTitle} />
      <meta name="twitter:description" content={description} />
      {openGraphImage && <meta name="twitter:image" content={openGraphImage.url} />}

      <CanonicalTags
        url={mainCanonicalUrl}
        default={canonicalTags?.default ?? mainCanonicalUrl}
        alternate={canonicalTags?.alternate}
      />
      {children}
    </NextHead>
  );
};
